import { useReactFlow } from '@xyflow/react';
import { useCallback } from 'react';

import { useEditCanvasStore } from '../store';
import { FIT_VIEW_PROGRAMMATIC_MIN_ZOOM } from '../utils/computeFitViewport';
import { fitCanvasToNodes } from '../utils/fitCanvasToNodes';
import { getFitViewPadding } from '../utils/getFitViewPadding';
import { usePrefersReducedMotionRef, ZOOM_TO_FIT_ANIMATION_DURATION_MS } from './usePrefersReducedMotionRef';

export type ZoomToFitParams = {
  nodeIds?: string[];
  withAnimation?: boolean;
};

export const DEFAULT_ZOOM_TO_FIT_PARAMS: ZoomToFitParams = {
  nodeIds: undefined,
  withAnimation: false,
};

export const useZoomToFit = () => {
  const { fitView, getZoom, setViewport } = useReactFlow();
  const prefersReducedMotionRef = usePrefersReducedMotionRef();

  const zoomToFit = useCallback(
    (params: ZoomToFitParams = {}) => {
      const mergedParams = { ...DEFAULT_ZOOM_TO_FIT_PARAMS, ...params };

      const { nodeIds, withAnimation } = mergedParams;
      const maxZoom = Math.max(getZoom(), 1);

      requestAnimationFrame(() => {
        if (!nodeIds) {
          fitCanvasToNodes({
            maxZoom,
            nodes: useEditCanvasStore.getState().nodes,
            setViewport,
          });
          return;
        }

        fitView({
          duration: withAnimation && !prefersReducedMotionRef.current ? ZOOM_TO_FIT_ANIMATION_DURATION_MS : 0,
          maxZoom,
          minZoom: FIT_VIEW_PROGRAMMATIC_MIN_ZOOM,
          nodes: nodeIds.map((id) => ({ id })),
          padding: getFitViewPadding(),
        });
      });
    },
    [fitView, getZoom, prefersReducedMotionRef, setViewport],
  );

  return { zoomToFit };
};
